import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CalendarPlus, Check, CheckCheck, CircleAlert, Clock, Handshake, Lock, Tag, UserRoundPlus } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { get, post, textoDeErroGravado } from '../../nucleo/api';
import { dataHora, email as fmtEmail, hora, nomeDeArquivo, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useDiscreto } from '../../componentes/sensivel';
import { AnexosMensagem } from './AnexosMensagem';
import { EmailHtml } from './EmailHtml';
import { useSessao } from '../../nucleo/sessao';
import { AgendarModal, ClassificarModal, EncerrarConfirmar, OportunidadeModal, TransferirModal } from './AcoesConversa';
import { Composer } from './Composer';
import { useIntervaloConversas } from './dados';
import { anexosDaMensagem, autorDoPortal, contatoDoCabecalho, linkNoGmail, nomeDoContato, type Conversa, type DadosEmail, type EstadoCanais, type Mensagem, type PaginaApi, type StatusMensagem } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { LinkParceiro } from '../../componentes/link-parceiro';
import { carregarMais } from '../../nucleo/foco';

/** Trilha 5b (Digisac): prioridade fora do normal, etiquetas e o menu de entrada esperando a escolha (lista e cabeçalho). */
export function SelosConversa({ conversa }: { conversa: Pick<Conversa, 'prioridade' | 'etiquetas' | 'menu_pendente'> }) {
  const { t } = useTranslation('conversas');
  const p = conversa.prioridade ?? 'normal';
  const etiquetas = conversa.etiquetas ?? [];
  if (p === 'normal' && !etiquetas.length && !conversa.menu_pendente) return null;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1">
      {p !== 'normal' && <Selo cor={p === 'urgente' ? 'perigo' : p === 'alta' ? 'laranja' : 'neutro'}>{t(`t5b.prioridades.${p}`)}</Selo>}
      {conversa.menu_pendente && <Selo cor="azul">{t('t5b.menuPendente')}</Selo>}
      {etiquetas.map((e) => (
        <span key={e} className="min-w-min">
          <Selo cor="acento" quebra>
            {e}
          </Selo>
        </span>
      ))}
    </span>
  );
}

const ICONE: Record<StatusMensagem, typeof Check> = { enviando: Clock, enviado: Check, entregue: CheckCheck, lido: CheckCheck, falhou: CircleAlert };

function Status({ m }: { m: Mensagem }) {
  const { t } = useTranslation('conversas');
  const Icone = ICONE[m.status];
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${m.status === 'falhou' ? 'text-perigo' : m.status === 'lido' ? 'text-ciano' : 'text-tinta-3'}`}>
      <Icone className="size-3.5" aria-hidden />
      {t(`status.${m.status}`)}
    </span>
  );
}

/** U11 (lote 1): a retenção da caixa tirou o corpo da mensagem; fica o aviso e, com o Message-ID, o link para o Gmail. */
function CorpoRemovido({ meses, caixa, messageId }: { meses: number; caixa: string | null | undefined; messageId: string | null | undefined }) {
  const { t } = useTranslation('conversas');
  const link = linkNoGmail(caixa, messageId);
  return (
    <p className="text-xs text-tinta-3" data-corpo-removido>
      {t('u11.corpoRemovido', { count: meses })}{' '}
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer" className="font-medium text-acento-forte underline">
          {t('u11.abrirNoGmail')}
        </a>
      )}
    </p>
  );
}

const mesesDaRetencao = (dados: Record<string, unknown>) => {
  const r = dados.retencao as { meses?: unknown } | undefined;
  return r && typeof r === 'object' && typeof r.meses === 'number' ? r.meses : null;
};

/** Fase 2 (W9, F1d): cabeçalho e corpo do e-mail recebido (HTML isolado; no modo discreto, só o texto censurado). */
function CorpoEmail({ m, email, nome, caixa }: { m: Mensagem; email: DadosEmail; nome: string; caixa?: string | null }) {
  const { t } = useTranslation('conversas');
  const { ativo } = useDiscreto();
  const de = email.de_nome ? `${nomePessoa(email.de_nome)} <${fmtEmail(email.de)}>` : fmtEmail(email.de);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 flex-col gap-0.5 text-xs">
        <p className="min-w-0 break-words text-tinta-2">
          <span className="text-tinta-3">{t('w9.de')}: </span>
          {de}
        </p>
        {email.assunto && (
          <p className="min-w-0 break-words font-medium text-tinta-1">
            <span className="font-normal text-tinta-3">{t('w9.assunto')}: </span>
            {textoLivre(email.assunto, nome, email.de_nome)}
          </p>
        )}
      </div>
      {mesesDaRetencao(m.dados) !== null ? (
        <CorpoRemovido meses={mesesDaRetencao(m.dados)!} caixa={caixa} messageId={email.message_id} />
      ) : email.html && !ativo ? (
        <EmailHtml html={email.html} remotas={email.imagens_remotas ?? 0} titulo={t('w9.conteudoEmail', { de: email.de_nome ? nomePessoa(email.de_nome) : fmtEmail(email.de) })} />
      ) : (
        <>
          <p className="text-sm break-words whitespace-pre-wrap text-tinta-1">{textoLivre(m.conteudo, nome, email.de_nome)}</p>
          {email.html && ativo && <p className="text-xs text-tinta-3">{t('w9.htmlDiscreto')}</p>}
        </>
      )}
    </div>
  );
}

/** Arquivos que não entraram (tamanho, tipo pelos bytes, excedente), com o motivo no idioma. */
function Recusados({ itens, nome }: { itens: { nome: string; motivo: string }[]; nome: string }) {
  const { t } = useTranslation('conversas');
  if (!itens.length) return null;
  return (
    <div className="text-xs text-tinta-3">
      <p className="font-medium text-tinta-2">{t('w9.anexosRecusados')}</p>
      <ul className="list-inside list-disc">
        {itens.map((r, i) => (
          <li key={i} className="break-words">
            {nomeDeArquivo(r.nome, nome)}: {t(`w9.midiaErro.${r.motivo}`, { defaultValue: t('w9.midiaErro.indisponivel') })}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Bolha memorizada (r11 robustez M1): a consulta de 4 s mantém o mesmo objeto da mensagem que não mudou, e essa bolha não refaz a censura do texto; a troca do modo e do idioma chega pelo `useTranslation`. */
const Bolha = memo(function Bolha({ m, nome, caixa }: { m: Mensagem; nome: string; caixa?: string | null }) {
  const { t } = useTranslation('conversas');
  if (m.tipo === 'sistema') {
    return (
      <li className="flex justify-center">
        <span className="rounded-full border border-borda px-3 py-1 text-center text-xs text-tinta-3">
          {textoLivre(m.conteudo)} <span className="numero">{hora(m.created_at)}</span>
        </span>
      </li>
    );
  }
  const saida = m.direcao === 'saida';
  const email = m.direcao === 'entrada' && m.dados.email && typeof m.dados.email === 'object' ? (m.dados.email as DadosEmail) : null;
  const anexos = anexosDaMensagem(m.dados);
  const midiaErro = typeof m.dados.midia_erro === 'string' ? m.dados.midia_erro : null;
  const recusados = Array.isArray(m.dados.anexos_recusados) ? (m.dados.anexos_recusados as { nome: string; motivo: string }[]) : [];
  const rotuloMidia = m.tipo === 'midia' && /^\[[^\]]+\]$/.test(m.conteudo.trim());
  const autorPortal = autorDoPortal(m);
  return (
    <li className={`flex ${saida ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex min-w-0 flex-col gap-1.5 rounded-2xl border px-3.5 py-2 ${email ? 'w-full max-w-2xl' : 'max-w-full sm:max-w-md'} ${saida ? 'border-acento-forte bg-acento-suave' : 'border-borda bg-sup-2'}`}>
        {m.tipo === 'template' && <span className="kicker">{t('acoes.template')}</span>}
        {email ? <CorpoEmail m={m} email={email} nome={nome} caixa={caixa} /> : !(rotuloMidia && anexos.length) && m.conteudo && <p className="text-sm break-words whitespace-pre-wrap text-tinta-1">{textoLivre(m.conteudo, nome)}</p>}
        {!email && mesesDaRetencao(m.dados) !== null && <CorpoRemovido meses={mesesDaRetencao(m.dados)!} caixa={caixa} messageId={(m.dados.email as DadosEmail | undefined)?.message_id} />}
        <AnexosMensagem conversaId={m.conversa_id} anexos={anexos} nomes={[nome]} />
        {m.tipo === 'midia' && !anexos.length && (midiaErro ? <p className="text-xs text-tinta-3">{t(`w9.midiaErro.${midiaErro}`, { defaultValue: t('w9.midiaErro.indisponivel') })}</p> : m.direcao === 'entrada' && <p className="text-xs text-tinta-3">{t('painel.midia')}</p>)}
        <Recusados itens={recusados} nome={nome} />
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-0.5">
          {saida && m.enviada_por_nome && <span className="text-xs text-tinta-3">{m.enviada_por_nome}</span>}
          {autorPortal && (
            <span className="text-xs text-tinta-3" data-autor-portal="">
              {autorPortal}
            </span>
          )}
          <span className="numero text-xs text-tinta-3" title={dataHora(m.created_at)}>
            {hora(m.created_at)}
          </span>
          {saida && <Status m={m} />}
        </div>
        {m.status === 'falhou' && m.erro && <p className="text-xs break-words text-perigo">{textoDeErroGravado(m.erro)}</p>}
      </div>
    </li>
  );
});

/** Conversa à direita: cabeçalho com ações, mensagens e campo de envio. */
export function PainelConversa({ conversa, estado, aoVoltar }: { conversa: Conversa; estado: EstadoCanais | undefined; aoVoltar: () => void }) {
  const { t } = useTranslation('conversas');
  useDiscreto(); // Review r63 (VIS-M1): o endereço da caixa passa pelo `email()` e redesenha quando o discreto muda.
  const s = useSessao();
  const qc = useQueryClient();
  const [transferir, setTransferir] = useState(false);
  const [encerrar, setEncerrar] = useState(false);
  const [oportunidade, setOportunidade] = useState(false);
  const [agendar, setAgendar] = useState(false);
  const [classificar, setClassificar] = useState(false);
  const fim = useRef<HTMLDivElement>(null);
  const conectado = !!estado?.[conversa.canal];
  const intervalo = useIntervaloConversas(4000);

  const q = useInfiniteQuery({
    queryKey: ['conversas', 'mensagens', conversa.id],
    queryFn: ({ pageParam }) => get<PaginaApi<Mensagem>>(`/conversas/conversas/${conversa.id}/mensagens`, { cursor: pageParam || undefined, limite: 60 }),
    initialPageParam: '',
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
    refetchInterval: intervalo,
  });
  // As páginas vêm da mais nova para a mais antiga; a tela mostra da mais antiga para a mais nova.
  const mensagens = useMemo(() => (q.data ? [...q.data.pages].reverse().flatMap((p) => [...p.itens].reverse()) : []), [q.data]);
  const ultimoId = mensagens[mensagens.length - 1]?.id;

  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
  }, [ultimoId, conversa.id]);

  const lida = useMutation({
    mutationFn: () => post(`/conversas/conversas/${conversa.id}/ler`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversas', 'lista'] }),
  });
  const { nao_lidas } = conversa;
  const marcar = lida.mutate;
  useEffect(() => {
    // Review r4: zerar as não lidas é de quem atende (conversas.enviar); quem só consulta não some com o contador.
    if (nao_lidas > 0 && s.pode('conversas.enviar')) marcar();
    // Marca como lida ao abrir e quando chega mensagem nova com a conversa aberta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversa.id, nao_lidas]);

  const encerrada = conversa.estado === 'encerrada';
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-borda px-3 py-3">
        <Botao variante="fantasma" tamanho="sm" className="md:hidden" aria-label={t('painel.voltar')} icone={<ArrowLeft className="size-4" />} onClick={aoVoltar} />
        <div className="min-w-0 flex-1 basis-48">
          <p className="flex min-w-0 items-center gap-2">
            {/* W20: contato de uma empresa que a pessoa não vê: o rótulo neutro, sem o link (a ficha seria 404). */}
            {conversa.parceiro_id ? (
              <LinkParceiro id={conversa.parceiro_id} nome={conversa.parceiro_nome} title={t('painel.abrirFicha')} className="truncate font-semibold hover:text-acento-forte" />
            ) : (
              <span className="truncate font-semibold" title={nomeDoContato(conversa)}>{nomeDoContato(conversa)}</span>
            )}
            <Selo cor={conversa.estado === 'aberta' ? 'verde' : conversa.estado === 'pendente' ? 'laranja' : 'neutro'}>{t(`estados.${conversa.estado}`)}</Selo>
          </p>
          <SelosConversa conversa={conversa} />
          {conversa.canal === 'email' && <TextoCortado como="p" className="text-sm text-tinta-2">{conversa.assunto ? textoLivre(conversa.assunto, conversa.parceiro_nome, conversa.remetente_nome) : t('w9.semAssunto')}</TextoCortado>}
          <TextoCortado como="p" className="text-xs text-tinta-3">
            <span className={conversa.canal === 'portal' || conversa.canal === 'site' ? undefined : 'numero'} data-contato-conversa>
              {contatoDoCabecalho(conversa, t('painel.contatoPortal'))}
            </span>
            {' · '}
            {conversa.atendente_nome ? `${t('painel.atendente')}: ${conversa.atendente_nome}` : t('painel.semAtendente')}
            {conversa.fila_nome ? ` · ${t('w9.fila')}: ${conversa.fila_nome}` : ''}
            {conversa.canal === 'email' && conversa.caixa ? ` · ${t('w9.caixa')}: ${fmtEmail(conversa.caixa)}` : ''}
          </TextoCortado>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {s.pode('conversas.enviar') && (
            <Botao tamanho="sm" icone={<Tag className="size-4" />} onClick={() => setClassificar(true)}>
              {t('t5b.classificar')}
            </Botao>
          )}
          {s.pode('conversas.transferir') && !encerrada && (
            <Botao tamanho="sm" icone={<UserRoundPlus className="size-4" />} onClick={() => setTransferir(true)}>
              {t('acoes.transferir')}
            </Botao>
          )}
          {s.pode('conversas.editar') && (
            <>
              <Botao tamanho="sm" icone={<Handshake className="size-4" />} disabled={!estado?.pipeline} title={estado?.pipeline ? undefined : t('acoes.pipelineOff')} onClick={() => setOportunidade(true)}>
                {t('acoes.oportunidade')}
              </Botao>
              <Botao tamanho="sm" icone={<CalendarPlus className="size-4" />} disabled={!estado?.agenda} title={estado?.agenda ? undefined : t('acoes.agendaOff')} onClick={() => setAgendar(true)}>
                {t('acoes.agendar')}
              </Botao>
            </>
          )}
          {s.pode('conversas.encerrar') && !encerrada && (
            <Botao tamanho="sm" variante="perigo" icone={<Lock className="size-4" />} onClick={() => setEncerrar(true)}>
              {t('acoes.encerrar')}
            </Botao>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : mensagens.length === 0 ? (
          <Vazio texto={t('painel.semMensagens')} />
        ) : (
          <>
            {q.hasNextPage && (
              <div className="mb-3 flex justify-center">
                <Botao variante="fantasma" tamanho="sm" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget, { anteriores: true })}>
                  {t('painel.carregarAnteriores')}
                </Botao>
              </div>
            )}
            <ul className="flex flex-col gap-2">
              {mensagens.map((m) => (
                <Bolha key={m.id} m={m} nome={conversa.parceiro_nome ?? ''} caixa={conversa.caixa} />
              ))}
            </ul>
          </>
        )}
        <div ref={fim} />
      </div>

      <Composer key={conversa.id} conversa={conversa} conectado={conectado} oficial={['oficial', 'zernio'].includes(estado?.whatsapp_provedor ?? 'oficial')} />

      <TransferirModal conversa={conversa} aberto={transferir} aoFechar={() => setTransferir(false)} />
      {classificar && <ClassificarModal conversa={conversa} aberto={classificar} aoFechar={() => setClassificar(false)} />}
      <EncerrarConfirmar conversa={conversa} aberto={encerrar} aoFechar={() => setEncerrar(false)} />
      <OportunidadeModal conversa={conversa} aberto={oportunidade} aoFechar={() => setOportunidade(false)} />
      <AgendarModal conversa={conversa} aberto={agendar} aoFechar={() => setAgendar(false)} />
    </div>
  );
}
